import RemindAPI from 'lib/RemindAPI';
import { getApolloClientSingleton } from 'lib/apollo/client/browser';
import STREAM_SECTION_CHATS_QUERY from 'chat/components/StreamSectionList/queries/StreamSectionChatsQuery.gql';
import type { StreamSectionChatsVariables } from 'chat/components/StreamSectionList/queries/__generated__/StreamSectionChats';
import CLASS_STREAM_SECTIONS_QUERY from 'chat/components/StreamSectionList/queries/ClassStreamSectionsQuery.gql';
import ORG_STREAM_SECTIONS_QUERY from 'chat/components/StreamSectionList/queries/OrgStreamSectionsQuery.gql';
import PUT_MESSAGE_MUTATION from 'chat/queries/PutMessageMutation.gql';
import ErrorWithCode from 'lib/ErrorWithCode';

import {
  putMessage as putMessageDataType,
  putMessageVariables,
  putMessage_putMessage_messages_announcements_item_MessageItem as PutMessageItem,
} from 'chat/queries/__generated__/putMessage';
import SCHEDULE_MESSAGE_MUTATION from 'chat/queries/ScheduleMessageMutation.gql';
import {
  scheduleMessage as scheduleMessageDataType,
  scheduleMessageVariables,
  scheduleMessage_scheduleMessage_messages_announcements_item_MessageItem as ScheduleMessageItem,
} from 'chat/queries/__generated__/scheduleMessage';
import UPDATE_SCHEDULED_MESSAGE_MUTATION from 'chat/queries/UpdateScheduledMessageMutation.gql';
import {
  updateScheduledMessage as updateScheduledMessageDataType,
  updateScheduledMessageVariables,
  updateScheduledMessage_updateScheduledMessage_messages_announcements_item_MessageItem as UpdateScheduleMessageItem,
} from 'chat/queries/__generated__/updateScheduledMessage';
import { cloneDeep } from 'lodash';
import { convertObjectKeysToSnakeCase } from 'utils/camelCase';
import { dispatcher } from 'lib/Dispatcher';
import Message from 'entities/Message';
import { ActionTypes } from 'groups/constants/GroupConstants';
import { addAnnouncementStreamSequenceItem } from 'chat/components/AnnouncementStream';
import { addStreamSequenceItem, broadcastStreamSequenceItem } from 'chat/components/ChatStream';
import ChatActions from 'chat/actions/ChatActions';
import ToastActions from 'actions/ToastActions';

function GQLMessageItemToMessageJSON(
  messageItem: PutMessageItem | ScheduleMessageItem | UpdateScheduleMessageItem,
  seqNumber: string
) {
  const message = cloneDeep(messageItem);
  const groupId = message.group ? message.group.id : 123456789;
  // @ts-expect-error ts-migrate(2790) FIXME: The operand of a 'delete' operator must be optiona... Remove this comment to see the full error message
  delete message['group'];

  const threads = message.threads.map((thread) => ({
    ...thread,
    group_id: groupId,
  }));

  // TODO: refactor this to not use mutability so that the return type of
  // GQLMessageItemToMessageJSON is inferred correctly by TS
  message.linkPreviews.forEach((preview) => {
    if (preview.imageUrl) {
      Object.assign(preview, { image: preview.imageUrl });
      // @ts-expect-error ts-migrate(2790) FIXME: The operand of a 'delete' operator must be optiona... Remove this comment to see the full error message
      delete preview.imageUrl;
    }
  });

  // TODO: refactor this to not use mutability so that the return type of
  // GQLMessageItemToMessageJSON is inferred correctly by TS
  if (message && message.files && message.files.length > 0) {
    message.files.map((file) => {
      Object.assign(file, { id: file.uuid });
      return convertObjectKeysToSnakeCase(file, ['contentType', 'previewWidth', 'previewHeight']);
    });
  }

  return {
    ...message,
    id: message.uuid,
    group_ids: [groupId],
    seq: seqNumber,
    send_at: message.sendAt,
    sent_at: message.sentAt,
    can_react: message.canReact,
    created_at: message.createdAt,
    public_url: message.publicUrl,
    reaction_summary: message.reactionSummary,
    link_previews: message.linkPreviews,
    threads,
  };
}

export async function putMessage(variables: putMessageVariables) {
  // Sending a new message can cause a new group or organization-level chat stream to be
  // created, making the data backing the StreamSectionList component out of date. Specifying
  // refetchQueries for the queries responsible for that data causes the StreamSectionList
  // to update.
  const groupRecipients = variables.input.recipients.filter((r) => r.type === 'group');
  const orgRecipients = variables.input.recipients.filter((r) => r.type === 'district');
  const groupRefetchQueries = groupRecipients.map((r) => {
    return {
      query: CLASS_STREAM_SECTIONS_QUERY,
      variables: { classUuid: r.uuid },
    };
  });
  const orgRefetchQueries = orgRecipients.map((r) => {
    return {
      query: ORG_STREAM_SECTIONS_QUERY,
      variables: { orgUuid: r.uuid },
    };
  });

  const result = await getApolloClientSingleton().mutate<putMessageDataType, putMessageVariables>({
    mutation: PUT_MESSAGE_MUTATION,
    variables,
    refetchQueries: [
      ...groupRefetchQueries,
      ...orgRefetchQueries,
      // We need to refetch the list of "all conversations" (hence the groupId = -1), because if we are
      // sending to a new group then that group won't show otherwise
      {
        query: STREAM_SECTION_CHATS_QUERY,
        variables: { groupId: -1 } as StreamSectionChatsVariables,
      },
    ],
    awaitRefetchQueries: true,
  });

  const putMessage = result.data?.putMessage;

  if (!putMessage) {
    throw new Error();
  }
  if (putMessage.error) {
    throw new ErrorWithCode(putMessage.error.message, putMessage.error.code || '');
  }

  putMessage.messages?.announcements.forEach((announcement) => {
    const { item } = announcement;
    if (item.__typename !== 'MessageItem') {
      return;
    }

    const data = GQLMessageItemToMessageJSON(cloneDeep(item), announcement.seq);
    const message = Message.fromJSON(data);
    dispatcher.dispatch({
      type: ActionTypes.SEND_GROUP_MESSAGE,
      message,
    });

    try {
      if (item.sender.__typename === 'EntitySender') {
        // the stream UUID is the sender UUID for items sent by an entity
        const streamUUID = item.sender.uuid;
        addAnnouncementStreamSequenceItem(
          streamUUID,
          {
            __typename: 'SequenceItem',
            key: `${item.uuid}.${streamUUID}`,
            seq: announcement.seq,
            item,
          },
          { overrideSequenceNumber: true }
        );
      } else {
        item.threads.forEach((thread) => {
          const streamUUID = thread.uuid;
          addAnnouncementStreamSequenceItem(
            streamUUID,
            {
              __typename: 'SequenceItem',
              key: `${item.uuid}.${streamUUID}`,
              seq: announcement.seq,
              item,
            },
            { overrideSequenceNumber: true }
          );
        });
      }
    } catch (e) {
      console.debug(e);
      console.error('could not broadcast message into announcement streams', e);
    }

    if (item.sender.__typename !== 'EntitySender') {
      try {
        const streamUUID = item.group?.uuid;
        if (!streamUUID) {
          console.warn('bad group.uuid', item);
          return;
        }

        const streamId = item.group?.id;
        if (!streamId) {
          console.warn('bad group.id', item);
          return;
        }

        // add the announcement into all the chat stream that
        // are connected to the same group as the announcement
        // if it is not sent by an entity
        broadcastStreamSequenceItem(
          streamUUID,
          streamId,
          {
            ...announcement,
            // TODO probably not needed
            __typename: 'SequenceItem',
            key: `${item.uuid}.${streamUUID}`,
            // TODO probably not needed
            item,
          },
          { overrideSequenceNumber: true }
        );
      } catch (e) {
        console.debug(e);
        console.error('could not broadcast message into chat streams');
      }
    }
  });

  // add the chat messages into all the chat stream.
  putMessage.messages?.chatMessages.forEach((item) => {
    try {
      if (item.item.__typename === 'MessageItem') {
        addStreamSequenceItem(item.item.chat?.uuid, item, { overrideSequenceNumber: true });
      }
    } catch (e) {
      console.debug(e);
      console.error('could not add chat message into chat stream');
    }
  });

  // We redirect to an individual chat if we were sending a chat message to one or more individual recipients.
  const needToRedirectToIndividualChat =
    putMessage.messages?.announcements.length === 0 &&
    putMessage.messages?.chatMessages.length >= 1;
  if (
    needToRedirectToIndividualChat &&
    putMessage.messages?.chatMessages[0].item.__typename === 'MessageItem'
  ) {
    const chatUuid = putMessage.messages?.chatMessages[0].item.chat?.uuid;
    chatUuid && ChatActions.selectChat(chatUuid);
  }

  return putMessage;
}

export function scheduleMessage(variables: scheduleMessageVariables) {
  // Scheduling a new message can cause a new group or organization-level chat stream to be
  // created, making the data backing the StreamSectionList component out of date. Specifying
  // refetchQueries for the queries responsible for that data causes the StreamSectionList
  // to update.
  const groupRecipients = variables.input.messageInput.recipients.filter((r) => r.type === 'group');
  const districtRecipients = variables.input.messageInput.recipients.filter(
    (r) => r.type === 'district'
  );
  const groupRefetchQueries = groupRecipients.map((r) => {
    return {
      query: CLASS_STREAM_SECTIONS_QUERY,
      variables: { classUuid: r.uuid },
    };
  });
  const districtRefetchQueries = districtRecipients.map((r) => {
    return {
      query: ORG_STREAM_SECTIONS_QUERY,
      variables: { orgUuid: r.uuid },
    };
  });
  return RemindAPI.execute<scheduleMessageDataType>({
    mutation: SCHEDULE_MESSAGE_MUTATION,
    variables,
    refetchQueries: [...groupRefetchQueries, ...districtRefetchQueries],
  }).then(({ scheduleMessage }) => {
    if (scheduleMessage.error) {
      throw new ErrorWithCode(scheduleMessage.error.message, scheduleMessage.error.code || '');
    }

    scheduleMessage.messages?.announcements.forEach((announcement) => {
      if (announcement.item.__typename !== 'MessageItem') {
        return;
      }

      let item = cloneDeep(announcement.item);
      item = GQLMessageItemToMessageJSON(item, announcement.seq);
      const message = Message.fromJSON(item);
      dispatcher.dispatch({
        type: ActionTypes.SEND_GROUP_SCHEDULED_MESSAGE,
        message,
      });
    });
    return scheduleMessage;
  });
}

export function updateScheduledMessage(variables: updateScheduledMessageVariables) {
  return RemindAPI.execute<updateScheduledMessageDataType>({
    mutation: UPDATE_SCHEDULED_MESSAGE_MUTATION,
    variables,
  }).then(({ updateScheduledMessage }) => {
    if (updateScheduledMessage.error) {
      throw new ErrorWithCode(
        updateScheduledMessage.error.message,
        updateScheduledMessage.error.code || ''
      );
    }
    ToastActions.showSuccessToast('Announcement updated');
    return updateScheduledMessage;
  });
}

const actions = { putMessage, scheduleMessage, updateScheduledMessage };

export default actions;
